import React, { useState, memo } from 'react';
import PropTypes from 'prop-types';
import { logger } from '../utils/logger';
import { storage } from '../utils/storage';
import { ERROR_MESSAGES } from '../constants/messages';

// Enhanced fallback component with comprehensive error recovery options
const SimpleFallback = memo(({ 
  message = ERROR_MESSAGES.SOMETHING_WENT_WRONG, 
  showRetry = true,
  onRetry,
  error,
  errorId,
  retryCount = 0,
  maxRetries = 3,
  showDetails = false,
  showReportError = true
}) => {
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [isReporting, setIsReporting] = useState(false);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleStartFresh = () => {
    storage.clear();
    sessionStorage.clear();
    window.location.href = '/emmys-learning-app';
  };

  const handleReportError = async () => {
    setIsReporting(true);
    try {
      // Create error report
      const errorReport = {
        errorId,
        message: error?.message || message,
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        retryCount,
        userId: storage.get('userId') || 'anonymous'
      };

      // In a real app, send to error reporting service
      logger.debug('Error Report:', errorReport);
      
      // Copy to clipboard as fallback
      await navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2));
      
      logger.info(ERROR_MESSAGES.ERROR_DETAILS_COPIED);
      // Could trigger a toast notification here instead
    } catch (reportError) {
      logger.error('Failed to report error:', reportError);
      // Could trigger an error toast here instead
    } finally {
      setIsReporting(false);
    }
  };

  const getErrorIcon = () => {
    if (error?.name === 'ChunkLoadError') return '📦';
    if (error?.message?.includes('network')) return '🌐';
    if (error?.message?.includes('timeout')) return '⏰';
    return '😅';
  };

  const getRecoveryMessage = () => {
    if (retryCount > 0) {
      return ERROR_MESSAGES.RETRY_ATTEMPT(retryCount, maxRetries);
    }
    return ERROR_MESSAGES.DONT_WORRY;
  };

  const canRetry = showRetry && retryCount < maxRetries;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="text-center text-white p-8 max-w-md w-full">
        <div className="text-6xl mb-4">{getErrorIcon()}</div>
        <h1 className="text-3xl font-bold mb-4">{ERROR_MESSAGES.OOPS}</h1>
        <p className="text-lg mb-4">{message}</p>
        <p className="text-sm mb-6 opacity-75">{getRecoveryMessage()}</p>
        
        <div className="space-y-3">
          {canRetry && (
            <button
              onClick={handleRetry}
              className="w-full bg-white text-purple-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              {ERROR_MESSAGES.TRY_AGAIN_WITH_COUNT(retryCount, maxRetries)}
            </button>
          )}
          
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-purple-600 text-white px-6 py-3 rounded-full font-bold hover:bg-purple-700 transition-colors"
          >
            🔄 {ERROR_MESSAGES.RELOAD_PAGE}
          </button>
          
          <button
            onClick={handleStartFresh}
            className="w-full bg-purple-700 text-white px-6 py-3 rounded-full font-bold hover:bg-purple-800 transition-colors"
          >
            🏠 {ERROR_MESSAGES.START_FRESH}
          </button>

          {showReportError && (
            <button
              onClick={handleReportError}
              disabled={isReporting}
              className="w-full bg-red-500 text-white px-6 py-3 rounded-full font-bold hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              📤 {isReporting ? ERROR_MESSAGES.REPORTING : ERROR_MESSAGES.REPORT_ERROR}
            </button>
          )}
        </div>

        {(showDetails || error) && (
          <div className="mt-6">
            <button
              onClick={() => setShowErrorDetails(!showErrorDetails)}
              className="text-white/70 hover:text-white text-sm underline"
            >
              {showErrorDetails ? ERROR_MESSAGES.HIDE_ERROR_DETAILS : ERROR_MESSAGES.SHOW_ERROR_DETAILS}
            </button>
            
            {showErrorDetails && (
              <div className="mt-4 p-4 bg-black/20 rounded-lg text-left">
                <div className="text-xs space-y-2">
                  {errorId && (
                    <div>
                      <span className="font-semibold">{ERROR_MESSAGES.ERROR_ID}</span> {errorId}
                    </div>
                  )}
                  {error?.name && (
                    <div>
                      <span className="font-semibold">{ERROR_MESSAGES.ERROR_TYPE}</span> {error.name}
                    </div>
                  )}
                  {error?.message && (
                    <div>
                      <span className="font-semibold">{ERROR_MESSAGES.ERROR_MESSAGE}</span> {error.message}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">{ERROR_MESSAGES.ERROR_URL}</span> {window.location.href}
                  </div>
                  <div>
                    <span className="font-semibold">{ERROR_MESSAGES.ERROR_TIME}</span> {new Date().toLocaleString()}
                  </div>
                  {retryCount > 0 && (
                    <div>
                      <span className="font-semibold">{ERROR_MESSAGES.ERROR_RETRIES}</span> {retryCount}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

SimpleFallback.propTypes = {
  message: PropTypes.string,
  showRetry: PropTypes.bool,
  onRetry: PropTypes.func,
  error: PropTypes.object,
  errorId: PropTypes.string,
  retryCount: PropTypes.number,
  maxRetries: PropTypes.number,
  showDetails: PropTypes.bool,
  showReportError: PropTypes.bool
};

SimpleFallback.defaultProps = {
  message: ERROR_MESSAGES.SOMETHING_WENT_WRONG,
  showRetry: true,
  onRetry: null,
  error: null,
  errorId: null,
  retryCount: 0,
  maxRetries: 3,
  showDetails: false,
  showReportError: true
};

SimpleFallback.displayName = 'SimpleFallback';

export default SimpleFallback;
